"use client";

import { useEffect, type RefObject } from "react";

type NativeProvider = "youtube" | "dailymotion" | "vimeo";

type NativeEmbed = {
  iframe: HTMLIFrameElement;
  provider: NativeProvider;
};

function resolveProvider(src: string): NativeProvider | null {
  if (/youtube(?:-nocookie)?\.com\/embed/i.test(src)) return "youtube";
  if (/dailymotion\.com\/embed\/video/i.test(src)) return "dailymotion";
  if (/player\.vimeo\.com\/video/i.test(src)) return "vimeo";
  return null;
}

function ensureNativeControls(
  iframe: HTMLIFrameElement,
  provider: NativeProvider,
) {
  const rawSrc = iframe.getAttribute("src") || iframe.src;
  if (!rawSrc) return;

  try {
    const url = new URL(rawSrc, window.location.href);
    url.searchParams.set("controls", "1");
    if (provider === "youtube") {
      url.searchParams.set("playsinline", "1");
      url.searchParams.set("enablejsapi", "1");
      url.searchParams.set("origin", window.location.origin);
    } else if (provider === "vimeo") {
      url.searchParams.set("playsinline", "1");
      url.searchParams.set("api", "1");
    } else {
      url.searchParams.set("api", "postMessage");
    }
    const normalized = url.toString();
    if (normalized !== iframe.src) iframe.src = normalized;
  } catch {
    /* Leave a provider URL untouched when it cannot be normalized safely. */
  }
}

function pauseNativeEmbed({ iframe, provider }: NativeEmbed) {
  const target = iframe.contentWindow;
  if (!target) return;

  try {
    const targetOrigin = new URL(iframe.src).origin;
    if (provider === "youtube") {
      target.postMessage(
        JSON.stringify({ event: "command", func: "pauseVideo", args: [] }),
        targetOrigin,
      );
    } else if (provider === "vimeo") {
      target.postMessage(JSON.stringify({ method: "pause" }), targetOrigin);
    } else {
      target.postMessage("pause", "*");
    }
  } catch {
    /* Cross-provider lifecycle commands are best-effort. */
  }
}

/**
 * Keep provider embeds native while stopping off-screen playback. Shorts use
 * their own Plyr-based controller and never pass through this hook.
 */
export function useNativeProviderEmbeds(
  containerRef: RefObject<HTMLElement | null>,
  contentKey: string,
): void {
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const embeds = Array.from(
      root.querySelectorAll<HTMLIFrameElement>("iframe"),
    )
      .map((iframe): NativeEmbed | null => {
        const provider = resolveProvider(
          iframe.getAttribute("src") || iframe.src,
        );
        if (!provider) return null;
        ensureNativeControls(iframe, provider);
        return { iframe, provider };
      })
      .filter((embed): embed is NativeEmbed => Boolean(embed));
    if (!embeds.length) return;

    const observers: IntersectionObserver[] = [];
    if (typeof IntersectionObserver !== "undefined") {
      embeds.forEach((embed) => {
        const observer = new IntersectionObserver(
          ([entry]) => {
            if (!entry.isIntersecting || entry.intersectionRatio < 0.25) {
              pauseNativeEmbed(embed);
            }
          },
          { threshold: [0, 0.25, 0.6] },
        );
        observer.observe(embed.iframe);
        observers.push(observer);
      });
    }

    const pauseWhenHidden = () => {
      if (document.hidden) embeds.forEach(pauseNativeEmbed);
    };
    document.addEventListener("visibilitychange", pauseWhenHidden);

    return () => {
      document.removeEventListener("visibilitychange", pauseWhenHidden);
      observers.forEach((observer) => observer.disconnect());
      embeds.forEach(pauseNativeEmbed);
    };
  }, [containerRef, contentKey]);
}
